import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { ScbApiService } from './scb-api.service';
import { ScbSettings } from './scb.models';

@Injectable({ providedIn: 'root' })
export class ScbSettingsService {
  private readonly api = inject(ScbApiService);
  private readonly settingsSubject = new BehaviorSubject<ScbSettings | null>(null);
  private readonly enabledSubject = new BehaviorSubject<boolean>(false);
  private loaded = false;
  private loading$: Observable<ScbSettings | null> | null = null;

  readonly settings$ = this.settingsSubject.asObservable();

  getSettings(force = false): Observable<ScbSettings | null> {
    if (this.loaded && !force) {
      return of(this.settingsSubject.value);
    }
    if (!this.loading$ || force) {
      this.loading$ = this.api.getSettings().pipe(
        map((resp) => resp?.data ?? null),
        tap((s) => {
          this.loaded = true;
          this.settingsSubject.next(s);
          this.enabledSubject.next(!!s?.isEnabled);
          this.loading$ = null;
        }),
        catchError(() => {
          this.loaded = true;
          this.settingsSubject.next(null);
          this.enabledSubject.next(false);
          this.loading$ = null;
          return of(null);
        })
      );
    }
    return this.loading$;
  }

  refresh(): Observable<ScbSettings | null> {
    this.loaded = false;
    this.loading$ = null;
    return this.getSettings(true);
  }

  peek(): ScbSettings | null {
    return this.settingsSubject.value;
  }

  isEnabled$(): Observable<boolean> {
    if (!this.loaded) {
      this.getSettings().subscribe();
    }
    return this.enabledSubject.asObservable();
  }

  /** Call after successful settings save when response body is known. */
  applyLocal(settings: ScbSettings | null): void {
    this.loaded = true;
    this.settingsSubject.next(settings);
    this.enabledSubject.next(!!settings?.isEnabled);
  }
}
